import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, Sparkles, ArrowLeftRight, Lock, ShieldCheck } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import axios from 'axios';

const API_URL = window.location.origin;

// Privacy welcome message for first-time users
const PRIVACY_WELCOME = {
  role: 'assistant',
  content: `Welcome, Sister 💎

Your private reflections are not visible to other Members and are used only to personalize your experience within the App.

We do not sell your data. Your journal entries, goals, and conversations with me stay in your personal space.

How can I support you today?`,
  isWelcome: true
};

// Generate a unique session ID
const generateSessionId = () => {
  const stored = sessionStorage.getItem('she_session_id');
  if (stored) return stored;
  const newId = `she_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('she_session_id', newId);
  return newId;
};

// Get stored position preference
const getStoredPosition = () => {
  const stored = localStorage.getItem('she_chat_position');
  return stored === 'left' ? 'left' : 'right';
};

const SHEChatWidget = ({ pageContext = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(getStoredPosition);
  const [hasSeenPrivacyWelcome, setHasSeenPrivacyWelcome] = useState(() => {
    return localStorage.getItem('she_privacy_welcome_seen') === 'true';
  });
  const [messages, setMessages] = useState(() => {
    // Show privacy welcome for first-time users
    const seenWelcome = localStorage.getItem('she_privacy_welcome_seen') === 'true';
    if (!seenWelcome) {
      return [PRIVACY_WELCOME];
    }
    return [
      {
        role: 'assistant',
        content: "I AM SHE — your Sister Diamonds guide. I'm here to help you navigate, answer questions, or simply hold space. What's on your mind?"
      }
    ];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Mark privacy welcome as seen after first interaction
  useEffect(() => {
    if (!hasSeenPrivacyWelcome && messages.length > 1) {
      localStorage.setItem('she_privacy_welcome_seen', 'true');
      setHasSeenPrivacyWelcome(true);
    }
  }, [messages, hasSeenPrivacyWelcome]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const togglePosition = () => {
    const newPosition = position === 'right' ? 'left' : 'right';
    setPosition(newPosition);
    localStorage.setItem('she_chat_position', newPosition);
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Include auth token to get personalized context
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API_URL}/api/chat/she`, {
        message: userMessage,
        session_id: sessionId,
        page_context: pageContext
      }, { headers });

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having a moment — my connection flickered. Would you try that again?" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Position classes
  const isLeft = position === 'left';
  const buttonPositionClass = isLeft ? 'left-6' : 'right-6';
  const windowPositionClass = isLeft ? 'left-6' : 'right-6';
  const slideAnimation = isLeft ? 'animate-in slide-in-from-left-4' : 'animate-in slide-in-from-bottom-4';

  return (
    <>
      {/* Chat Button */}
      <div className={`fixed bottom-6 ${buttonPositionClass} z-[9999] flex flex-col items-${isLeft ? 'start' : 'end'} gap-2`}>
        {/* Position Toggle - Only visible when chat is closed */}
        {!isOpen && (
          <button
            onClick={togglePosition}
            className="bg-white/90 hover:bg-white text-gray-600 hover:text-forest p-2 rounded-full shadow-md border border-gray-200 transition-all hover:scale-105"
            title={`Move to ${isLeft ? 'right' : 'left'} side`}
            data-testid="she-chat-move"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>
        )}
        
        {/* Main Chat Button - Compact Size */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`transition-all duration-300 flex items-center gap-2 ${
            isOpen 
              ? 'bg-gray-700 hover:bg-gray-600 h-10 w-10 rounded-full justify-center shadow-lg' 
              : 'bg-forest hover:bg-forest/90 hover:shadow-xl hover:scale-105 px-3 py-2 rounded-full shadow-[0_4px_16px_rgba(0,0,0,0.25)] border border-terracotta'
          }`}
          data-testid="she-chat-toggle"
          aria-label={isOpen ? 'Close chat' : 'Open chat with SHE'}
        >
          {isOpen ? (
            <X className="h-4 w-4 text-white" />
          ) : (
            <>
              <div className="relative">
                <MessageCircle className="h-4 w-4 text-white" />
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-terracotta rounded-full animate-pulse shadow-sm border border-white" />
              </div>
              <span className="text-white font-semibold text-xs tracking-wide drop-shadow-sm">SHE</span>
            </>
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-24 ${windowPositionClass} z-[9999] w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden ${slideAnimation} duration-300`}
          data-testid="she-chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-forest to-forest/90 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white">SHE</h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-xs text-white/80 flex items-center gap-1 cursor-help">
                          <Lock className="h-3 w-3" />
                          Not Shared
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[250px] text-center">
                        <p className="text-xs">Your conversations with SHE are not visible to other Members and are used only to personalize your experience.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              {/* Move button in header when open */}
              <button
                onClick={togglePosition}
                className="text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                title={`Move to ${isLeft ? 'right' : 'left'} side`}
                data-testid="she-chat-move-header"
              >
                <ArrowLeftRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[350px] overflow-y-auto p-4 space-y-4 bg-cream/30">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    message.role === 'user'
                      ? 'bg-forest text-white rounded-br-md'
                      : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 bg-white border-t border-gray-100">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask SHE anything..."
                className="flex-1 border-gray-200 focus:border-forest focus:ring-forest/20"
                disabled={isLoading}
                data-testid="she-chat-input"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!input.trim() || isLoading}
                className="bg-terracotta hover:bg-terracotta/90 text-white h-10 w-10"
                data-testid="she-chat-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default SHEChatWidget;
